/** Shots, bombs and the stage badges (docs/spec/04 §1). */

import { BLUE, CYAN, GOLD, ORANGE, RED, SKY, WHITE, YELLOW } from '../palette';
import type { SpriteDef } from './types';

/** The Lancer's shot: 2 x 8, drawn in a 4 x 8 cell so it turns about its middle (Spread). */
export const SHOT: SpriteDef = {
  frames: [['.WW.', '.WW.', '.WW.', '.CC.', '.CC.', '.SS.', '.SS.', '.BB.']],
  colours: { W: WHITE, C: CYAN, S: SKY, B: BLUE },
  rotates: true,
};

/** A piercing shot: longer and hotter. */
export const SHOT_PIERCE: SpriteDef = {
  frames: [['.WW.', 'WWWW', 'WWWW', '.YY.', '.YY.', '.OO.', '.OO.', '.RR.', '.RR.', '.RR.']],
  colours: { W: WHITE, Y: YELLOW, O: ORANGE, R: RED },
  rotates: true,
};

/** An enemy bomb: 3 x 7, two frames that swap as it falls. */
export const BOMB: SpriteDef = {
  frames: [
    ['.Y.', 'RYR', '.R.', 'RYR', '.R.', 'RYR', '.Y.'],
    ['.R.', 'YRY', '.Y.', 'YRY', '.Y.', 'YRY', '.R.'],
  ],
  colours: { R: RED, Y: YELLOW },
};

/** Stage badges, by worth. Each is 8 wide; the big ones read as medals, the small as pennants. */
const BADGE_ART: Readonly<Record<number, readonly string[]>> = {
  1: [
    '........', '........', '........', '........', '.W......', '.WRR....', '.WRRRR..', '.WRRRRRR',
    '.WRRRR..', '.WRR....', '.W......', '.W......', '.W......', '.W......', '........', '........',
  ],
  5: [
    '........', '........', '.W......', '.WYYYY..', '.WYYYYYY', '.WYYYY..', '.W......', '.WRRRR..',
    '.WRRRRRR', '.WRRRR..', '.W......', '.W......', '.W......', '.W......', '........', '........',
  ],
  10: [
    '........', '........', '.SSSSSS.', 'SSSSSSSS', 'SWWSSWWS', 'SSWSWSSW', 'SSWSWSSW', 'SSWSWSSW',
    'SWWWSWWS', 'SSSSSSSS', '.SSSSSS.', '..SSSS..', '...SS...', '........', '........', '........',
  ],
  20: [
    '........', '........', '.YYYYYY.', 'YYYYYYYY', 'YRRYYRRY', 'YYYRRYYR', 'YYRYRYYR', 'YRYYRYYR',
    'YRRRYRRY', 'YYYYYYYY', '.YYYYYY.', '..YYYY..', '...YY...', '........', '........', '........',
  ],
  30: [
    '........', '........', '.RRRRRR.', 'RRRRRRRR', 'RWWRRWWR', 'RRRWWRRW', 'RRWWWRRW', 'RRRWWRRW',
    'RWWRRWWR', 'RRRRRRRR', '.RRRRRR.', '..RRRR..', '...RR...', '........', '........', '........',
  ],
  50: [
    '...GG...', '...GG...', 'GGGGGGGG', '.GGGGGG.', '..GGGG..', '.GGGGGG.', '.GG..GG.', 'GG....GG',
    '........', 'WWWWWWWW', 'WCCWWCCW', 'WCWWWCWC', 'WCCWWCWC', 'WWCWWCWC', 'WCCWWCCW', 'WWWWWWWW',
  ],
};

const BADGE_COLOURS = { W: WHITE, R: RED, Y: YELLOW, S: SKY, G: GOLD, C: BLUE };

/** Badge worths, biggest first: the stage number is paid out in these (docs/spec/01 §1). */
export const BADGE_VALUES: readonly number[] = [50, 30, 20, 10, 5, 1];

export function badgeDef(value: number): SpriteDef {
  return { frames: [BADGE_ART[value] ?? BADGE_ART[1] ?? []], colours: BADGE_COLOURS };
}

/** The badges that spell out a stage number, biggest first. */
export function badgesFor(stage: number): number[] {
  const out: number[] = [];
  let left = Math.max(0, Math.floor(stage));
  for (const v of BADGE_VALUES) {
    while (left >= v) {
      out.push(v);
      left -= v;
    }
  }
  return out;
}
